import { useEffect, useMemo, useRef, useState } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { PaveSignature } from '../ui/PaveSignature';
import {
  Icone,
  Progression,
  TRACE_COCHE,
  TRACE_FLECHE_DROITE,
  TRACE_FLECHE_GAUCHE,
} from '../ui/AssistantEtapes';
import {
  FICHE_VIDE,
  type DossierEntretien,
  type FicheEntretien,
  type GrilleOuiNon,
  type LigneEntretien,
  type OptionFiche,
  type ReferentielFiche,
  type ReponseOuiNon,
} from '../../lib/useEntretiens';
import type { ModeDossier } from '../../lib/useApprenants';
import { useAuth } from '../../auth/useAuth';
import { PositionnementsCandidat } from '../immersion/PositionnementsCandidat';

function horodatage(valeur: string): string {
  return new Date(valeur).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

const CLASSE_SAISIE =
  'w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

/** Titre de rubrique, repris du document papier. */
function Rubrique({ titre, children }: { titre: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="space-y-3">
      <h3 className="border-b-2 border-codialis-mint pb-1 text-[11px] font-bold uppercase tracking-wider text-codialis-navy">
        {titre}
      </h3>
      {children}
    </section>
  );
}

function Libelle({ texte }: { texte: string }): JSX.Element {
  return (
    <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-gray">
      {texte}
    </span>
  );
}

/** Champ d'une ligne. Le type `date` sert les dates civiles du papier. */
function Texte({
  libelle,
  valeur,
  type = 'text',
  placeholder,
  onChange,
}: {
  libelle: string;
  valeur: string;
  type?: 'text' | 'date' | 'tel' | 'email';
  placeholder?: string;
  onChange: (valeur: string) => void;
}): JSX.Element {
  return (
    <label className="block">
      <Libelle texte={libelle} />
      <input
        type={type}
        value={valeur}
        placeholder={placeholder}
        onChange={(evenement) => onChange(evenement.target.value)}
        className={CLASSE_SAISIE}
      />
    </label>
  );
}

function Zone({
  libelle,
  valeur,
  lignes = 3,
  placeholder,
  onChange,
}: {
  libelle: string;
  valeur: string;
  lignes?: number;
  placeholder?: string;
  onChange: (valeur: string) => void;
}): JSX.Element {
  return (
    <label className="block">
      <Libelle texte={libelle} />
      <textarea
        rows={lignes}
        value={valeur}
        placeholder={placeholder}
        onChange={(evenement) => onChange(evenement.target.value)}
        className={CLASSE_SAISIE}
      />
    </label>
  );
}

/**
 * Liste déroulante. Les libellés viennent du référentiel servi par l'API :
 * l'écran affiche ce qu'on lui donne et enregistre le code.
 */
function Liste({
  libelle,
  options,
  valeur,
  vide = 'À préciser',
  onChange,
}: {
  libelle: string;
  options: { code: string; libelle: string }[];
  valeur: string;
  vide?: string;
  onChange: (valeur: string) => void;
}): JSX.Element {
  return (
    <label className="block">
      <Libelle texte={libelle} />
      <select
        value={valeur}
        onChange={(evenement) => onChange(evenement.target.value)}
        className={CLASSE_SAISIE}
      >
        <option value="">{vide}</option>
        {options.map((option) => (
          <option key={option.code} value={option.code}>
            {option.libelle}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Cases à cocher multiples, avec une case « Autre » facultative. */
function Cases({
  options,
  valeurs,
  onBasculer,
}: {
  options: OptionFiche[];
  valeurs: string[];
  onBasculer: (code: string) => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {options.map((option) => (
        <label
          key={option.code}
          className="flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy transition hover:border-codialis-sky/40"
        >
          <input
            type="checkbox"
            checked={valeurs.includes(option.code)}
            onChange={() => onBasculer(option.code)}
            className="h-4 w-4 shrink-0 accent-codialis-mint"
          />
          <span>{option.libelle}</span>
        </label>
      ))}
    </div>
  );
}

/**
 * Choix unique du papier. Un bouton par option, et un second clic décoche :
 * le document laisse la ligne vide tant que rien n'est coché, l'écran doit
 * pouvoir y revenir.
 */
function Choix({
  libelle,
  options,
  valeur,
  onChange,
}: {
  libelle: string;
  options: OptionFiche[];
  valeur: string;
  onChange: (valeur: string) => void;
}): JSX.Element {
  return (
    <div>
      <Libelle texte={libelle} />
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const actif = valeur === option.code;
          return (
            <button
              key={option.code}
              type="button"
              aria-pressed={actif}
              onClick={() => onChange(actif ? '' : option.code)}
              className={`min-h-[40px] rounded-xl border px-3 py-2 text-sm font-medium transition ${
                actif
                  ? 'border-codialis-mint bg-codialis-mint/20 text-codialis-navy'
                  : 'border-gray-200 bg-white text-codialis-gray hover:border-codialis-sky/40'
              }`}
            >
              {option.libelle}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Case oui/non du papier. Recliquer sur la réponse cochée la décoche. */
function OuiNon({
  libelle,
  valeur,
  onChange,
}: {
  libelle: string;
  valeur: ReponseOuiNon;
  onChange: (valeur: ReponseOuiNon) => void;
}): JSX.Element {
  return (
    <div>
      <Libelle texte={libelle} />
      <div className="flex gap-2">
        {(['OUI', 'NON'] as const).map((reponse) => {
          const actif = valeur === reponse;
          return (
            <button
              key={reponse}
              type="button"
              aria-pressed={actif}
              onClick={() => onChange(actif ? '' : reponse)}
              className={`min-h-[40px] min-w-0 flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition sm:flex-none sm:px-5 ${
                actif
                  ? 'border-codialis-mint bg-codialis-mint/20 text-codialis-navy'
                  : 'border-gray-200 bg-white text-codialis-gray hover:border-codialis-sky/40'
              }`}
            >
              {reponse === 'OUI' ? 'Oui' : 'Non'}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Grille oui/non : une ligne par option, comme le tableau du papier. */
function Grille({
  options,
  reponses,
  onRepondre,
}: {
  options: OptionFiche[];
  reponses: GrilleOuiNon;
  onRepondre: (code: string, reponse: 'OUI' | 'NON' | null) => void;
}): JSX.Element {
  return (
    <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
      {options.map((option) => (
        <div
          key={option.code}
          className="flex flex-wrap items-center justify-between gap-2 bg-white px-3 py-2"
        >
          <span className="text-sm text-codialis-navy">{option.libelle}</span>
          <div className="flex gap-1.5">
            {(['OUI', 'NON'] as const).map((reponse) => {
              const actif = reponses[option.code] === reponse;
              return (
                <button
                  key={reponse}
                  type="button"
                  aria-pressed={actif}
                  aria-label={`${option.libelle} : ${reponse === 'OUI' ? 'oui' : 'non'}`}
                  onClick={() => onRepondre(option.code, actif ? null : reponse)}
                  className={`min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] min-w-[56px] rounded-lg border px-3 text-xs font-semibold transition ${
                    actif
                      ? 'border-codialis-mint bg-codialis-mint/20 text-codialis-navy'
                      : 'border-gray-200 bg-white text-codialis-gray hover:border-codialis-sky/40'
                  }`}
                >
                  {reponse === 'OUI' ? 'Oui' : 'Non'}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ChampAutre({
  valeur,
  onChange,
}: {
  valeur: string;
  onChange: (valeur: string) => void;
}): JSX.Element {
  return (
    <input
      aria-label="Autre : préciser"
      value={valeur}
      onChange={(evenement) => onChange(evenement.target.value)}
      placeholder="Autre : préciser"
      className={CLASSE_SAISIE}
    />
  );
}

/**
 * Pré-remplissage par le dossier : identité, coordonnées, pièce d'identité et
 * responsable légal ont déjà été saisis à l'inscription, et les redemander au
 * candidat qui vient de les donner est exactement ce que la saisie unique doit
 * supprimer (CDC §4.1).
 *
 * Seules les rubriques restées vides sont remplies : une fiche déjà enregistrée
 * garde ce que le chargé de recrutement y a écrit, y compris quand il a corrigé
 * une valeur du dossier. La correction vit dans la fiche — le dossier se
 * modifie depuis le dossier, ou par la bascule contrat / formation ci-dessous.
 */
function prerempliParLeDossier(
  fiche: FicheEntretien,
  dossier: DossierEntretien | null,
  formationDossier: string | null,
): FicheEntretien {
  if (!dossier) return fiche;
  const repris = (saisi: string, duDossier: string): string =>
    saisi.trim() !== '' ? saisi : duDossier;
  const identite = dossier.identite;

  return {
    ...fiche,
    formationDemandee: repris(fiche.formationDemandee, formationDossier ?? ''),
    identite: {
      ...fiche.identite,
      nomNaissance: repris(fiche.identite.nomNaissance, identite.nomNaissance),
      prenoms: repris(fiche.identite.prenoms, identite.prenoms),
      nomUsage: repris(fiche.identite.nomUsage, identite.nomUsage),
      dateNaissance: repris(fiche.identite.dateNaissance, identite.dateNaissance),
      communeNaissance: repris(fiche.identite.communeNaissance, identite.communeNaissance),
      nationalite: repris(fiche.identite.nationalite, identite.nationalite),
      telephone: repris(fiche.identite.telephone, identite.telephone),
      mail: repris(fiche.identite.mail, identite.mail),
      adresse: repris(fiche.identite.adresse, identite.adresse),
      codePostal: repris(fiche.identite.codePostal, identite.codePostal),
      ville: repris(fiche.identite.ville, identite.ville),
    },
    familiale: {
      ...fiche.familiale,
      // Le responsable légal ne concerne que les mineurs : reprendre celui d'un
      // dossier devenu majeur ferait réapparaître les coordonnées d'un tiers.
      responsableLegal: dossier.mineur
        ? repris(fiche.familiale.responsableLegal, dossier.responsableLegal)
        : fiche.familiale.responsableLegal,
    },
  };
}

/**
 * Fiche d'entretien de recrutement (document TOP FORMATION du 16/07/2026),
 * remplie pendant l'entretien.
 *
 * Les cases à cocher viennent du référentiel servi par l'API : l'écran affiche
 * des libellés qu'il ne connaît pas, et enregistre des codes. La fiche est
 * envoyée entière à chaque enregistrement — c'est ce qui permet d'effacer une
 * rubrique, ce qu'une fusion partielle côté serveur rendrait impossible.
 *
 * Deux sorties : « Enregistrer » garde la fiche sans clore l'entretien, et
 * « Terminer l'entretien » l'enregistre en marquant l'entretien réalisé. La
 * décision — retenu, non retenu, à revoir — se prend ensuite, depuis le
 * compte-rendu : mener l'entretien et trancher sont deux gestes distincts.
 *
 * Un entretien d'entreprise n'a pas de fiche de recrutement : le document porte
 * sur un candidat. L'écran n'y montre alors que les observations et les besoins
 * recueillis, plutôt qu'un formulaire sans objet.
 */
export function ModaleFicheEntretien({
  entretien,
  referentiel,
  formations,
  peutModifierDossier,
  enCours,
  erreur,
  onEnregistrer,
  onFermer,
}: {
  entretien: LigneEntretien;
  referentiel: ReferentielFiche;
  /** Catalogue servi par l'API : le rattachement se choisit dedans, pas au clavier. */
  formations: { code: string; titre: string }[];
  /** L'écran « Dossier apprenant » est ouvert à ce compte : sinon, pas de bascule. */
  peutModifierDossier: boolean;
  enCours: boolean;
  erreur: string | null;
  onEnregistrer: (
    valeurs: {
      fiche: FicheEntretien;
      compteRendu: string;
      besoinsRecueillis?: string;
      /** Présent seulement si le contrat ou la formation ont changé. */
      dossier?: { mode: ModeDossier; formationCode: string | null };
    },
    terminer: boolean,
  ) => void;
  onFermer: () => void;
}): JSX.Element {
  const [fiche, setFiche] = useState<FicheEntretien>(() =>
    prerempliParLeDossier(entretien.fiche ?? FICHE_VIDE, entretien.dossier, entretien.formation),
  );
  const [mode, setMode] = useState<ModeDossier>(entretien.dossier?.mode ?? 'CFA');
  const [formationCode, setFormationCode] = useState(entretien.dossier?.formationCode ?? '');
  const [compteRendu, setCompteRendu] = useState(entretien.compteRendu ?? '');
  const [besoins, setBesoins] = useState(entretien.besoinsRecueillis ?? '');

  const estCandidat = entretien.type === 'CANDIDAT';
  const { peutAcceder } = useAuth();
  // Les immersions sont celles du dossier, enregistrées tel qu'il est en base :
  // un dossier OF ne se positionne pas, même basculé à l'écran avant
  // l'enregistrement de la fiche.
  const apprenantImmersions =
    estCandidat &&
    entretien.apprenantId !== null &&
    entretien.dossier?.mode === 'CFA' &&
    (peutAcceder('com-matching') || peutAcceder('periode-immersion'))
      ? entretien.apprenantId
      : null;

  const [etape, setEtape] = useState(0);
  const haut = useRef<HTMLDivElement>(null);

  // Le document papier tient sur sept pages : les dérouler d'un seul tenant
  // pendant l'entretien oblige à chercher sa ligne en défilant, la personne en
  // face attendant. Les rubriques sont donc parcourues étape par étape, dans
  // l'ordre du papier et avec le même fil que l'inscription — un seul
  // assistant à réapprendre pour toute la plateforme.
  //
  // Un entretien d'entreprise n'a pas de fiche de recrutement : il ne reste que
  // les observations, donc une seule étape et aucun fil à afficher.
  const etapes = useMemo(
    () =>
      estCandidat
        ? [
            'Identité',
            'Situation',
            'Ressources',
            'Parcours',
            'Projet',
            'Compétences',
            'Disponibilités',
            ...(apprenantImmersions ? ['Immersions'] : []),
            'Conclusion',
          ]
        : ['Observations'],
    [estCandidat, apprenantImmersions],
  );

  const etapeCourante = Math.min(etape, etapes.length - 1);
  const derniere = etapeCourante === etapes.length - 1;

  // Changer d'étape sans remonter laisserait le haut du formulaire suivant
  // au-dessus de la fenêtre : la fenêtre est plus haute que l'écran, et c'est
  // la page qui défile.
  useEffect(() => {
    haut.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [etapeCourante]);

  /** Modifie une rubrique imbriquée sans toucher aux autres. */
  const majRubrique = <Cle extends keyof FicheEntretien>(
    cle: Cle,
    valeurs: Partial<FicheEntretien[Cle]>,
  ): void =>
    setFiche((actuelle) => ({
      ...actuelle,
      [cle]: { ...(actuelle[cle] as object), ...(valeurs as object) },
    }));

  const basculerCode = <Cle extends keyof FicheEntretien>(
    cle: Cle,
    champ: string,
    liste: string[],
  ) => (code: string): void =>
    majRubrique(cle, {
      [champ]: liste.includes(code) ? liste.filter((valeur) => valeur !== code) : [...liste, code],
    } as Partial<FicheEntretien[Cle]>);

  const repondreGrille = <Cle extends keyof FicheEntretien>(
    cle: Cle,
    champ: string,
    grille: GrilleOuiNon,
  ) => (code: string, reponse: 'OUI' | 'NON' | null): void => {
    const suivante: GrilleOuiNon = { ...grille };
    if (reponse === null) delete suivante[code];
    else suivante[code] = reponse;
    majRubrique(cle, { [champ]: suivante } as Partial<FicheEntretien[Cle]>);
  };

  // Le contrat et la formation ne partent au dossier que s'ils ont changé : un
  // enregistrement de fiche ne doit pas réécrire un dossier qu'on n'a pas
  const dossierModifie =
    entretien.dossier !== null &&
    (mode !== entretien.dossier.mode ||
      (formationCode || null) !== entretien.dossier.formationCode);

  const dateAujourdhui = useMemo(() => {
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Indian/Reunion',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date());
  }, []);

  const lieuSignature = useMemo(() => {
    const s = entretien.sites?.[0];
    if (!s) return 'l’antenne';
    return s.toLowerCase().startsWith('antenne') ? s : `Antenne ${s}`;
  }, [entretien.sites]);

  const dateSignatureAffichee = fiche.signatures?.dateSignature || dateAujourdhui;

  const majSignature = (role: 'candidat' | 'chargeRecrutement', trace: string | null) => {
    setFiche((actuelle) => {
      const signaturesActuelles = actuelle.signatures ?? {
        candidat: null,
        chargeRecrutement: null,
        dateSignature: dateSignatureAffichee,
      };
      return {
        ...actuelle,
        signatures: {
          ...signaturesActuelles,
          [role]: trace,
          dateSignature: dateSignatureAffichee,
        },
      };
    });
  };

  const valeurs = () => ({
    fiche: {
      ...fiche,
      signatures: {
        candidat: fiche.signatures?.candidat ?? null,
        chargeRecrutement: fiche.signatures?.chargeRecrutement ?? null,
        dateSignature: fiche.signatures?.dateSignature || dateSignatureAffichee,
      },
    },
    compteRendu,
    ...(dossierModifie ? { dossier: { mode, formationCode: formationCode || null } } : {}),
    // Les besoins d'une entreprise n'existent pas sur un entretien de candidat :
    // envoyer une chaîne vide effacerait une saisie faite ailleurs.
    ...(estCandidat ? {} : { besoinsRecueillis: besoins }),
  });

  return (
    <Modale
      titre={
        entretien.clos
          ? "Modifier la fiche d’entretien"
          : entretien.fiche
            ? "Reprendre la fiche d’entretien"
            : "Commencer l'entretien"
      }
      sousTitre={`${entretien.interlocuteur} · ${horodatage(entretien.dateEntretien)}`}
      ouverte
      onFermer={onFermer}
      largeur="max-w-4xl"
    >
      <div ref={haut} className="scroll-mt-24" aria-hidden="true" />

      {etapes.length > 1 && (
        <Progression etapes={etapes} courante={etapeCourante} onAller={setEtape} />
      )}

      <div className="space-y-6 px-4 sm:px-6 py-5">
        <div className="grid grid-cols-1 gap-3 rounded-xl bg-codialis-sand px-4 py-3 text-xs text-codialis-gray sm:grid-cols-2">
          <div>
            <span className="font-bold uppercase tracking-wide">Formation au dossier</span>
            <div className="text-codialis-navy">{entretien.formation ?? '—'}</div>
          </div>
          <div>
            <span className="font-bold uppercase tracking-wide">Entretien réalisé par</span>
            <div className="text-codialis-navy">{entretien.charge ?? '—'}</div>
          </div>
        </div>

        {estCandidat && etapes[etapeCourante] === 'Identité' && (
          <>
            <Rubrique titre="Entretien">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Liste
                  libelle="Orienté par"
                  options={referentiel.orienteurs}
                  valeur={fiche.orientePar}
                  vide="Non précisé"
                  onChange={(valeur) => setFiche((actuelle) => ({ ...actuelle, orientePar: valeur }))}
                />
                {fiche.orientePar === 'AUTRE' ? (
                  <Texte
                    libelle="Préciser"
                    valeur={fiche.orienteParPrecision}
                    onChange={(valeur) =>
                      setFiche((actuelle) => ({ ...actuelle, orienteParPrecision: valeur }))
                    }
                  />
                ) : (
                  <Texte
                    libelle="Formation demandée"
                    valeur={fiche.formationDemandee}
                    onChange={(valeur) =>
                      setFiche((actuelle) => ({ ...actuelle, formationDemandee: valeur }))
                    }
                  />
                )}
              </div>
              {fiche.orientePar === 'AUTRE' && (
                <Texte
                  libelle="Formation demandée"
                  valeur={fiche.formationDemandee}
                  onChange={(valeur) =>
                    setFiche((actuelle) => ({ ...actuelle, formationDemandee: valeur }))
                  }
                />
              )}

              {/* Le type de contrat et la formation sont des données du
                  dossier, pas de la fiche : l'entretien est le moment où elles
                  se tranchent, mais elles partent au dossier par sa propre
                  route, qui vérifie le droit. Un commercial « relation
                  entreprise » n'a pas cet écran : il ne voit pas la bascule. */}
              {entretien.dossier && peutModifierDossier && (
                <div className="space-y-3 rounded-xl border border-dashed border-codialis-sky/40 bg-white px-4 py-3">
                  <p className="text-[11px] text-codialis-gray">
                    Rattachement du dossier. Une modification ici est enregistrée sur le dossier
                    apprenant, avec la fiche.
                  </p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Liste
                      libelle="Type de contrat"
                      options={[
                        { code: 'CFA', libelle: 'Apprentissage (CFA, CERFA)' },
                        { code: 'OF', libelle: 'Formation continue (OF, certificat)' },
                      ]}
                      valeur={mode}
                      vide="Inchangé"
                      onChange={(valeur) => valeur !== '' && setMode(valeur as ModeDossier)}
                    />
                    <Liste
                      libelle="Formation au dossier"
                      options={formations.map((formation) => ({
                        code: formation.code,
                        libelle: `${formation.titre} (${formation.code})`,
                      }))}
                      valeur={formationCode}
                      vide="Aucune"
                      onChange={setFormationCode}
                    />
                  </div>
                </div>
              )}
            </Rubrique>

            <Rubrique titre="Informations personnelles">
              {entretien.dossier && (
                <p className="rounded-xl bg-codialis-sand px-3 py-2 text-[11px] text-codialis-gray">
                  Repris du dossier apprenant. Une correction faite ici reste sur la fiche
                  d’entretien : elle ne modifie pas le dossier.
                </p>
              )}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Texte
                  libelle="Nom de naissance"
                  valeur={fiche.identite.nomNaissance}
                  onChange={(valeur) => majRubrique('identite', { nomNaissance: valeur })}
                />
                <Texte
                  libelle="Prénoms"
                  valeur={fiche.identite.prenoms}
                  onChange={(valeur) => majRubrique('identite', { prenoms: valeur })}
                />
                <Texte
                  libelle="Nom d’usage"
                  valeur={fiche.identite.nomUsage}
                  onChange={(valeur) => majRubrique('identite', { nomUsage: valeur })}
                />
                <Texte
                  libelle="Date de naissance"
                  type="date"
                  valeur={fiche.identite.dateNaissance}
                  onChange={(valeur) => majRubrique('identite', { dateNaissance: valeur })}
                />
                <Texte
                  libelle="Commune de naissance"
                  valeur={fiche.identite.communeNaissance}
                  onChange={(valeur) => majRubrique('identite', { communeNaissance: valeur })}
                />
                <Texte
                  libelle="Nationalité"
                  valeur={fiche.identite.nationalite}
                  onChange={(valeur) => majRubrique('identite', { nationalite: valeur })}
                />
                <Texte
                  libelle="Téléphone"
                  type="tel"
                  valeur={fiche.identite.telephone}
                  placeholder="0692 55 12 09"
                  onChange={(valeur) => majRubrique('identite', { telephone: valeur })}
                />
                <Texte
                  libelle="Mail"
                  type="email"
                  valeur={fiche.identite.mail}
                  onChange={(valeur) => majRubrique('identite', { mail: valeur })}
                />
                <Texte
                  libelle="Pièce d’identité"
                  valeur={fiche.identite.pieceIdentite}
                  placeholder="CNI n° 123456789012"
                  onChange={(valeur) => majRubrique('identite', { pieceIdentite: valeur })}
                />
                <Texte
                  libelle="Carte de séjour"
                  valeur={fiche.identite.carteSejour}
                  placeholder="N° du titre de séjour"
                  onChange={(valeur) => majRubrique('identite', { carteSejour: valeur })}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
                <Texte
                  libelle="Adresse postale"
                  valeur={fiche.identite.adresse}
                  onChange={(valeur) => majRubrique('identite', { adresse: valeur })}
                />
                <Texte
                  libelle="Code postal"
                  valeur={fiche.identite.codePostal}
                  onChange={(valeur) => majRubrique('identite', { codePostal: valeur })}
                />
                <Texte
                  libelle="Ville"
                  valeur={fiche.identite.ville}
                  onChange={(valeur) => majRubrique('identite', { ville: valeur })}
                />
              </div>

              <Choix
                libelle="Logement"
                options={referentiel.logements}
                valeur={fiche.logement.statut}
                onChange={(valeur) => majRubrique('logement', { statut: valeur })}
              />
              <ChampAutre
                valeur={fiche.logement.autre}
                onChange={(valeur) => majRubrique('logement', { autre: valeur })}
              />

              <Choix
                libelle="Situation familiale"
                options={referentiel.situationsFamiliales}
                valeur={fiche.familiale.statut}
                onChange={(valeur) => majRubrique('familiale', { statut: valeur })}
              />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Texte
                  libelle="Nombre d’enfants"
                  valeur={fiche.familiale.nombreEnfants}
                  onChange={(valeur) => majRubrique('familiale', { nombreEnfants: valeur })}
                />
                <Texte
                  libelle="Âges des enfants"
                  valeur={fiche.familiale.agesEnfants}
                  onChange={(valeur) => majRubrique('familiale', { agesEnfants: valeur })}
                />
              </div>

              <Zone
                libelle="Personne à contacter en cas d’urgence"
                valeur={fiche.familiale.contactUrgence}
                lignes={2}
                placeholder="Nom, prénom, lien, coordonnées"
                onChange={(valeur) => majRubrique('familiale', { contactUrgence: valeur })}
              />
              <Zone
                libelle="Coordonnées du responsable légal, si mineur"
                valeur={fiche.familiale.responsableLegal}
                lignes={2}
                onChange={(valeur) => majRubrique('familiale', { responsableLegal: valeur })}
              />
              <OuiNon
                libelle="Suivi de justice"
                valeur={fiche.familiale.suiviJustice}
                onChange={(valeur) => majRubrique('familiale', { suiviJustice: valeur })}
              />
            </Rubrique>

          </>
        )}

        {estCandidat && etapes[etapeCourante] === 'Situation' && (
          <>
            <Rubrique titre="Situation actuelle">
              <Cases
                options={referentiel.situationsActuelles}
                valeurs={fiche.situation.statuts}
                onBasculer={basculerCode('situation', 'statuts', fiche.situation.statuts)}
              />
              <ChampAutre
                valeur={fiche.situation.autre}
                onChange={(valeur) => majRubrique('situation', { autre: valeur })}
              />

              {/* Donnée de santé (RGPD), comme la situation de handicap : elle
                  ne sort que par les routes gardées. L'aide au dossier ne se
                  pose qu'à un candidat sans reconnaissance. */}
              <OuiNon
                libelle="Reconnaissance RQTH"
                valeur={fiche.situation.rqth}
                onChange={(valeur) => majRubrique('situation', { rqth: valeur })}
              />
              {fiche.situation.rqth === 'NON' && (
                <>
                  <OuiNon
                    libelle="Aide à la constitution du dossier RQTH"
                    valeur={fiche.situation.aideDossierRqth}
                    onChange={(valeur) => majRubrique('situation', { aideDossierRqth: valeur })}
                  />
                  <Zone
                    libelle="Commentaire sur le dossier RQTH"
                    valeur={fiche.situation.aideDossierRqthCommentaire}
                    lignes={2}
                    onChange={(valeur) =>
                      majRubrique('situation', { aideDossierRqthCommentaire: valeur })
                    }
                  />
                </>
              )}

              <Choix
                libelle="Permis de conduire"
                options={referentiel.etatsPermis}
                valeur={fiche.mobilite.permis}
                onChange={(valeur) => majRubrique('mobilite', { permis: valeur })}
              />
              <Texte
                libelle="Précision sur le permis"
                valeur={fiche.mobilite.permisPrecision}
                onChange={(valeur) => majRubrique('mobilite', { permisPrecision: valeur })}
              />
              <div>
                <Libelle texte="Type de locomotion" />
                <Cases
                  options={referentiel.moyensLocomotion}
                  valeurs={fiche.mobilite.locomotion}
                  onBasculer={basculerCode('mobilite', 'locomotion', fiche.mobilite.locomotion)}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <ChampAutre
                  valeur={fiche.mobilite.locomotionAutre}
                  onChange={(valeur) => majRubrique('mobilite', { locomotionAutre: valeur })}
                />
                <Texte
                  libelle="Zone de déplacement"
                  valeur={fiche.mobilite.zoneDeplacement}
                  onChange={(valeur) => majRubrique('mobilite', { zoneDeplacement: valeur })}
                />
              </div>
            </Rubrique>

          </>
        )}

        {estCandidat && etapes[etapeCourante] === 'Ressources' && (
          <>
            <Rubrique titre="Ressources">
              <div>
                <Libelle texte="Allocations de la CAF" />
                <Cases
                  options={referentiel.allocationsCaf}
                  valeurs={fiche.ressources.allocationsCaf}
                  onBasculer={basculerCode(
                    'ressources',
                    'allocationsCaf',
                    fiche.ressources.allocationsCaf,
                  )}
                />
              </div>
              <ChampAutre
                valeur={fiche.ressources.allocationAutre}
                onChange={(valeur) => majRubrique('ressources', { allocationAutre: valeur })}
              />

              <OuiNon
                libelle="Inscription à France Travail"
                valeur={fiche.ressources.franceTravail}
                onChange={(valeur) => majRubrique('ressources', { franceTravail: valeur })}
              />
              {fiche.ressources.franceTravail === 'OUI' && (
                <>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Texte
                      libelle="Nom du conseiller(ère) France Travail"
                      valeur={fiche.ressources.conseillerFranceTravail}
                      onChange={(valeur) =>
                        majRubrique('ressources', { conseillerFranceTravail: valeur })
                      }
                    />
                    <Texte
                      libelle="Identifiant France Travail"
                      valeur={fiche.ressources.identifiantFranceTravail}
                      onChange={(valeur) =>
                        majRubrique('ressources', { identifiantFranceTravail: valeur })
                      }
                    />
                  </div>
                  <Cases
                    options={referentiel.aidesFranceTravail}
                    valeurs={fiche.ressources.aidesFranceTravail}
                    onBasculer={basculerCode(
                      'ressources',
                      'aidesFranceTravail',
                      fiche.ressources.aidesFranceTravail,
                    )}
                  />
                  <ChampAutre
                    valeur={fiche.ressources.aideAutre}
                    onChange={(valeur) => majRubrique('ressources', { aideAutre: valeur })}
                  />
                </>
              )}

              <OuiNon
                libelle="Inscription Mission Locale"
                valeur={fiche.ressources.missionLocale}
                onChange={(valeur) => majRubrique('ressources', { missionLocale: valeur })}
              />
              {fiche.ressources.missionLocale === 'OUI' && (
                <Texte
                  libelle="Nom du conseiller(ère)"
                  valeur={fiche.ressources.conseillerMissionLocale}
                  onChange={(valeur) =>
                    majRubrique('ressources', { conseillerMissionLocale: valeur })
                  }
                />
              )}

              <OuiNon
                libelle="AAH"
                valeur={fiche.ressources.aah}
                onChange={(valeur) => majRubrique('ressources', { aah: valeur })}
              />
              <div>
                <Libelle texte="Couverture sociale" />
                <Cases
                  options={referentiel.couverturesSociales}
                  valeurs={fiche.ressources.couvertureSociale}
                  onBasculer={basculerCode(
                    'ressources',
                    'couvertureSociale',
                    fiche.ressources.couvertureSociale,
                  )}
                />
              </div>
            </Rubrique>

          </>
        )}

        {estCandidat && etapes[etapeCourante] === 'Parcours' && (
          <>
            <Rubrique titre="Parcours professionnels">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Texte
                  libelle="Niveau de formation"
                  valeur={fiche.parcours.niveauFormation}
                  onChange={(valeur) => majRubrique('parcours', { niveauFormation: valeur })}
                />
                <Texte
                  libelle="Dernier diplôme obtenu"
                  valeur={fiche.parcours.dernierDiplome}
                  onChange={(valeur) => majRubrique('parcours', { dernierDiplome: valeur })}
                />
              </div>
              <Zone
                libelle="Dernière formation ou titre professionnel obtenu"
                valeur={fiche.parcours.derniereFormation}
                lignes={2}
                onChange={(valeur) => majRubrique('parcours', { derniereFormation: valeur })}
              />
              <OuiNon
                libelle="Inscrit dans un CFA"
                valeur={fiche.parcours.inscritCfa}
                onChange={(valeur) => majRubrique('parcours', { inscritCfa: valeur })}
              />
              {fiche.parcours.inscritCfa === 'OUI' && (
                <Texte
                  libelle="Lequel"
                  valeur={fiche.parcours.cfaLequel}
                  onChange={(valeur) => majRubrique('parcours', { cfaLequel: valeur })}
                />
              )}
            </Rubrique>

            <Rubrique titre="Expériences professionnelles">
              <Texte
                libelle="Dernier emploi occupé"
                valeur={fiche.experiences.dernierEmploi}
                onChange={(valeur) => majRubrique('experiences', { dernierEmploi: valeur })}
              />
              <OuiNon
                libelle="A déjà travaillé dans ce domaine"
                valeur={fiche.experiences.dejaTravailleDomaine}
                onChange={(valeur) => majRubrique('experiences', { dejaTravailleDomaine: valeur })}
              />
              {fiche.experiences.dejaTravailleDomaine === 'OUI' && (
                <>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <Texte
                      libelle="Entreprise"
                      valeur={fiche.experiences.entreprise}
                      onChange={(valeur) => majRubrique('experiences', { entreprise: valeur })}
                    />
                    <Texte
                      libelle="Fonction"
                      valeur={fiche.experiences.fonction}
                      onChange={(valeur) => majRubrique('experiences', { fonction: valeur })}
                    />
                    <Texte
                      libelle="Durée"
                      valeur={fiche.experiences.duree}
                      onChange={(valeur) => majRubrique('experiences', { duree: valeur })}
                    />
                  </div>
                  <Zone
                    libelle="Principales missions"
                    valeur={fiche.experiences.missions}
                    onChange={(valeur) => majRubrique('experiences', { missions: valeur })}
                  />
                </>
              )}
            </Rubrique>

          </>
        )}

        {estCandidat && etapes[etapeCourante] === 'Projet' && (
          <>
            <Rubrique titre="Demande de formation et projet professionnel">
              <Texte
                libelle="Poste recherché"
                valeur={fiche.projet.posteRecherche}
                onChange={(valeur) => majRubrique('projet', { posteRecherche: valeur })}
              />
              <Zone
                libelle="Pourquoi avoir choisi cette formation"
                valeur={fiche.projet.motivation}
                onChange={(valeur) => majRubrique('projet', { motivation: valeur })}
              />
              <div>
                <Libelle texte="Objectifs à l’issue de cette formation" />
                <Cases
                  options={referentiel.objectifsFormation}
                  valeurs={fiche.projet.objectifs}
                  onBasculer={basculerCode('projet', 'objectifs', fiche.projet.objectifs)}
                />
              </div>
              <ChampAutre
                valeur={fiche.projet.objectifAutre}
                onChange={(valeur) => majRubrique('projet', { objectifAutre: valeur })}
              />
            </Rubrique>

          </>
        )}

        {estCandidat && etapes[etapeCourante] === 'Compétences' && (
          <>
            <Rubrique titre="Compétences et aptitudes">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <OuiNon
                  libelle="CV à jour"
                  valeur={fiche.competences.cvAJour}
                  onChange={(valeur) => majRubrique('competences', { cvAJour: valeur })}
                />
                <OuiNon
                  libelle="Sait parler de ses points forts / faibles"
                  valeur={fiche.competences.pointsForts}
                  onChange={(valeur) => majRubrique('competences', { pointsForts: valeur })}
                />
              </div>
              <Zone
                libelle="Précisions"
                valeur={fiche.competences.pointsFortsPrecision}
                lignes={2}
                onChange={(valeur) => majRubrique('competences', { pointsFortsPrecision: valeur })}
              />
              <div>
                <Libelle texte="Maîtrise des savoirs de base" />
                <Grille
                  options={referentiel.savoirsBase}
                  reponses={fiche.competences.savoirs}
                  onRepondre={repondreGrille('competences', 'savoirs', fiche.competences.savoirs)}
                />
              </div>
              <div>
                <Libelle texte="Compétences numériques" />
                <Grille
                  options={referentiel.outilsNumeriques}
                  reponses={fiche.competences.numeriques}
                  onRepondre={repondreGrille(
                    'competences',
                    'numeriques',
                    fiche.competences.numeriques,
                  )}
                />
              </div>
              <Zone
                libelle="Commentaires"
                valeur={fiche.competences.commentaires}
                lignes={2}
                onChange={(valeur) => majRubrique('competences', { commentaires: valeur })}
              />
            </Rubrique>

            {/* Donnée de santé (RGPD) : elle ne sort du dossier que par les
                routes gardées, et n'apparaît sur aucun export commercial. */}
            <Rubrique titre="Situation de handicap">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <OuiNon
                  libelle="Situation de handicap ou besoin d’aménagement"
                  valeur={fiche.handicap.situation}
                  onChange={(valeur) => majRubrique('handicap', { situation: valeur })}
                />
                <OuiNon
                  libelle="Souhaite rencontrer le référent handicap"
                  valeur={fiche.handicap.rencontrerReferent}
                  onChange={(valeur) => majRubrique('handicap', { rencontrerReferent: valeur })}
                />
              </div>
              <Zone
                libelle="Besoins exprimés"
                valeur={fiche.handicap.besoins}
                onChange={(valeur) => majRubrique('handicap', { besoins: valeur })}
              />
            </Rubrique>

          </>
        )}

        {estCandidat && etapes[etapeCourante] === 'Disponibilités' && (
          <>
            <Rubrique titre="Disponibilités et contraintes">
              <Choix
                libelle="Disponibilité"
                options={referentiel.disponibilites}
                valeur={fiche.disponibilites.disponibilite}
                onChange={(valeur) => majRubrique('disponibilites', { disponibilite: valeur })}
              />
              <OuiNon
                libelle="Disponible sur toute la durée de la formation"
                valeur={fiche.disponibilites.dureeFormation}
                onChange={(valeur) => majRubrique('disponibilites', { dureeFormation: valeur })}
              />
              <Zone
                libelle="Contraintes particulières"
                valeur={fiche.disponibilites.contraintesParticulieres}
                lignes={2}
                onChange={(valeur) =>
                  majRubrique('disponibilites', { contraintesParticulieres: valeur })
                }
              />
              <OuiNon
                libelle="Enquête métier ou PMSMP nécessaire"
                valeur={fiche.disponibilites.enqueteMetier}
                onChange={(valeur) => majRubrique('disponibilites', { enqueteMetier: valeur })}
              />
              <Texte
                libelle="Précision"
                valeur={fiche.disponibilites.enquetePrecision}
                onChange={(valeur) => majRubrique('disponibilites', { enquetePrecision: valeur })}
              />
              <div>
                <Libelle texte="Accepte de travailler" />
                <Grille
                  options={referentiel.conditionsTravail}
                  reponses={fiche.disponibilites.conditionsTravail}
                  onRepondre={repondreGrille(
                    'disponibilites',
                    'conditionsTravail',
                    fiche.disponibilites.conditionsTravail,
                  )}
                />
              </div>
              <Zone
                libelle="Autres remarques importantes"
                valeur={fiche.disponibilites.autresRemarques}
                lignes={2}
                onChange={(valeur) => majRubrique('disponibilites', { autresRemarques: valeur })}
              />
              <div>
                <Libelle texte="Contraintes pouvant avoir un impact sur la formation" />
                <Grille
                  options={referentiel.contraintesImpact}
                  reponses={fiche.disponibilites.contraintesImpact}
                  onRepondre={repondreGrille(
                    'disponibilites',
                    'contraintesImpact',
                    fiche.disponibilites.contraintesImpact,
                  )}
                />
              </div>
              <Zone
                libelle="Précisions"
                valeur={fiche.disponibilites.precisionsContraintes}
                lignes={2}
                onChange={(valeur) =>
                  majRubrique('disponibilites', { precisionsContraintes: valeur })
                }
              />
            </Rubrique>

          </>
        )}

        {apprenantImmersions && etapes[etapeCourante] === 'Immersions' && (
          <Rubrique titre="Immersions en entreprise">
            <PositionnementsCandidat
              apprenantId={apprenantImmersions}
              peutPositionner={peutAcceder('com-matching')}
              peutEtablirContrat={peutAcceder('adm-contrats-apprentissage')}
              peutCreerEntreprise={peutAcceder('com-entreprises')}
            />
          </Rubrique>
        )}

        {estCandidat && etapes[etapeCourante] === 'Conclusion' && (
          <>
            <Rubrique titre="Conclusion de l’entretien">
              <Zone
                libelle="Conclusion"
                valeur={fiche.conclusion}
                lignes={5}
                onChange={(valeur) => setFiche((actuelle) => ({ ...actuelle, conclusion: valeur }))}
              />
            </Rubrique>
          </>
        )}

        {(!estCandidat || etapes[etapeCourante] === 'Conclusion') && (
          <Rubrique titre="Observations du chargé de recrutement">
            <Zone
              libelle="Compte-rendu libre"
              valeur={compteRendu}
              onChange={setCompteRendu}
              placeholder="Ce qui a été dit, et qui n’entre dans aucune rubrique ci-dessus"
            />
            {!estCandidat && (
              <Zone
                libelle="Besoins recueillis"
                valeur={besoins}
                lignes={3}
                onChange={setBesoins}
                placeholder="Postes, profils, calendrier évoqués"
              />
            )}
          </Rubrique>
        )}

        {estCandidat && etapes[etapeCourante] === 'Conclusion' && (
          <Rubrique titre="Signatures électroniques de l’entretien">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-codialis-sand/70 px-4 py-2.5 text-xs text-codialis-gray">
                <span>
                  Mention légale :{' '}
                  <strong className="text-codialis-navy">
                    Fait à {lieuSignature}, le {dateSignatureAffichee}
                  </strong>
                </span>
                <span className="text-[11px] italic">
                  Les signatures figureront sur le compte-rendu d’entretien.
                </span>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-codialis-navy">
                        Signature de l’apprenant
                      </h4>
                      <p className="text-xs text-codialis-gray">{entretien.interlocuteur}</p>
                    </div>
                    {fiche.signatures?.candidat ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        Signé
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        En attente
                      </span>
                    )}
                  </div>
                  <PaveSignature
                    traceInitiale={fiche.signatures?.candidat ?? null}
                    onChanger={(trace) => majSignature('candidat', trace)}
                    desactive={enCours}
                  />
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-codialis-navy">
                        Signature du chargé de recrutement
                      </h4>
                      <p className="text-xs text-codialis-gray">
                        {entretien.charge || 'Chargé de recrutement'}
                      </p>
                    </div>
                    {fiche.signatures?.chargeRecrutement ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        Signé
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        En attente
                      </span>
                    )}
                  </div>
                  <PaveSignature
                    traceInitiale={fiche.signatures?.chargeRecrutement ?? null}
                    onChanger={(trace) => majSignature('chargeRecrutement', trace)}
                    desactive={enCours}
                  />
                </div>
              </div>
            </div>
          </Rubrique>
        )}

        {erreur && <MessageErreur message={erreur} />}
      </div>

      {/* Barre d'actions collante : sur tablette, le pouce reste en bas de
          l'écran alors qu'une étape peut dépasser la hauteur. « Enregistrer »
          reste offert à chaque étape — un entretien s'interrompt, et la saisie
          déjà faite ne doit pas dépendre d'être allé jusqu'à la conclusion. */}
      <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 rounded-b-2xl border-t border-gray-100 bg-white px-4 sm:px-6 py-4">
        <div className="flex items-center gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={enCours}>
            Annuler
          </BoutonSecondaire>
          {/* Offert à chaque étape, fiche close ou non : un entretien
              s'interrompt, et une correction faite depuis le dossier n'a pas à
              traverser les huit étapes pour être enregistrée. */}
          <BoutonSecondaire onClick={() => onEnregistrer(valeurs(), false)} disabled={enCours}>
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </BoutonSecondaire>
        </div>

        <div className="flex items-center gap-2">
          {etapeCourante > 0 && (
            <BoutonSecondaire onClick={() => setEtape(etapeCourante - 1)} disabled={enCours}>
              <span className="flex items-center gap-1.5">
                <Icone trace={TRACE_FLECHE_GAUCHE} classe="h-4 w-4" />
                Précédent
              </span>
            </BoutonSecondaire>
          )}
          {!derniere ? (
            <BoutonPrincipal onClick={() => setEtape(etapeCourante + 1)} disabled={enCours}>
              <span className="flex items-center gap-1.5">
                Suivant
                <Icone trace={TRACE_FLECHE_DROITE} classe="h-4 w-4" />
              </span>
            </BoutonPrincipal>
          ) : entretien.clos ? (
            // La décision (retenu, non retenu, réalisé) est déjà prise : ce
            // bouton corrige la fiche sans y retoucher. Rappeler « terminer »
            // ici enverrait resultat=REALISE et effacerait un « retenu » déjà
            // enregistré.
            <BoutonPrincipal onClick={() => onEnregistrer(valeurs(), false)} disabled={enCours}>
              {enCours ? 'Enregistrement…' : 'Enregistrer les modifications'}
            </BoutonPrincipal>
          ) : (
            <BoutonPrincipal onClick={() => onEnregistrer(valeurs(), true)} disabled={enCours}>
              <span className="flex items-center gap-1.5">
                <Icone trace={TRACE_COCHE} classe="h-4 w-4" />
                {enCours ? 'Enregistrement…' : 'Terminer l’entretien'}
              </span>
            </BoutonPrincipal>
          )}
        </div>
      </div>
    </Modale>
  );
}
